import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Building2, Globe, Users, Award, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const groupCompanies = [
  {
    name: 'GSM Corp',
    focus: 'Core Business Operations',
    description: 'The flagship company leading institutional infrastructure solutions across Maharashtra and India.',
    services: ['Furniture Solutions', 'Smart Classrooms', 'Infrastructure Development'],
    established: '2017',
  },
  {
    name: 'GPMM Enterprises',
    focus: 'Manufacturing & Trading',
    description: 'Specialized in manufacturing and trading of educational and institutional supplies.',
    services: ['Manufacturing', 'Trading', 'Supply Chain'],
    established: '2018',
  },
  {
    name: 'Ved Enterprises',
    focus: 'Educational Supplies',
    description: 'Dedicated to providing comprehensive educational supplies and academic materials.',
    services: ['Educational Materials', 'Lab Equipment', 'Library Solutions'],
    established: '2018',
  },
  {
    name: 'Adhya Enterprises',
    focus: 'Healthcare Solutions',
    description: 'Focused on healthcare infrastructure and medical equipment supply.',
    services: ['Medical Equipment', 'Hospital Infrastructure', 'Healthcare Supplies'],
    established: '2019',
  },
  {
    name: 'ADMS Technoways',
    focus: 'Technology Services',
    description: 'Technology-focused company delivering digital transformation solutions.',
    services: ['Software Development', 'IT Infrastructure', 'Digital Solutions'],
    established: '2019',
  },
  {
    name: 'Enteka Systems Pvt Ltd',
    focus: 'System Integration',
    description: 'Expert in system integration and turnkey project execution.',
    services: ['System Integration', 'Turnkey Projects', 'Consulting'],
    established: '2020',
  },
  {
    name: 'Mandate IT Solutions Pvt Ltd',
    focus: 'IT Infrastructure',
    description: 'Comprehensive IT infrastructure and networking solutions.',
    services: ['Network Solutions', 'IT Infrastructure', 'Support Services'],
    established: '2020',
  },
  {
    name: 'Aarambh Enterprises',
    focus: 'Startup Initiatives',
    description: 'Supporting new ventures and innovative business initiatives.',
    services: ['New Ventures', 'Innovation', 'Business Development'],
    established: '2021',
  },
  {
    name: 'VSM Enterprises',
    focus: 'Distribution Network',
    description: 'Managing distribution and logistics across multiple regions.',
    services: ['Distribution', 'Logistics', 'Regional Operations'],
    established: '2021',
  },
  {
    name: 'Eonixa Dynamics Pvt Ltd',
    focus: 'Innovation & Technology',
    description: 'Driving innovation and next-generation technology solutions.',
    services: ['Innovation Labs', 'Technology R&D', 'Future Solutions'],
    established: '2022',
  },
  {
    name: 'AHAD Solutions Pvt Ltd',
    focus: 'Specialized Solutions',
    description: 'Providing specialized solutions for complex institutional requirements.',
    services: ['Specialized Projects', 'Custom Solutions', 'Expert Services'],
    established: '2022',
  },
]

const partners = [
  { category: 'FMCG Distribution', partners: ['Hindustan Unilever (HUL)'] },
  { category: 'Jewellery & Watches', partners: ['Mia by Tanishq', 'World of Titan', 'Helios'] },
  { category: 'Food & Beverage', partners: ['Frozen Bottle', 'Momo Panda'] },
  { category: 'Fashion & Retail', partners: ['Blackberrys', 'TITAN IRTH'] },
]

const stats = [
  { icon: Building2, value: '11', label: 'Group Companies' },
  { icon: Users, value: '500+', label: 'Team Members' },
  { icon: Globe, value: '50+', label: 'Districts Covered' },
  { icon: Award, value: '7+', label: 'Years Experience' },
]

export default function GroupCompanies() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-900/30 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Building2 className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-graphite-300">Group Ecosystem</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Our <span className="gradient-text-gold">Group Companies</span>
            </h1>
            <p className="text-xl text-graphite-300">
              A diversified ecosystem of 11 specialized companies working together to deliver
              comprehensive solutions across sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-white/5">
        <div className="container-premium">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-accent-400" />
                <div className="stats-number">{stat.value}</div>
                <div className="text-graphite-400 text-sm mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Companies Grid */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-12">
            <h2 className="heading-md text-white mb-4">All <span className="gradient-text-gold">11</span> Companies</h2>
            <p className="text-graphite-400">Click on any company to learn more about their services</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupCompanies.map((company, index) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card variant="hover" className="h-full p-6 group cursor-pointer">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <span className="text-white font-bold text-sm">
                        {company.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-primary-400 transition-colors">
                        {company.name}
                      </h3>
                      <p className="text-sm text-primary-400">{company.focus}</p>
                    </div>
                  </div>

                  <p className="text-graphite-400 mb-4">{company.description}</p>

                  <div className="space-y-2 mb-4">
                    {company.services.map((service, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-graphite-300">
                        <TrendingUp className="w-3 h-3 text-secondary-400" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <span className="text-sm text-graphite-500">Est. {company.established}</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-secondary-950/20 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-md text-white mb-4"
            >
              Trusted <span className="gradient-text">Partnerships</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-graphite-400"
            >
              Authorized distributor and franchise partner for leading brands
            </motion.p>
          </div>

          {partners.map((category, index) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="mb-8"
            >
              <h3 className="text-lg font-semibold text-white mb-4">{category.category}</h3>
              <div className="flex flex-wrap gap-3">
                {category.partners.map((partner, i) => (
                  <span
                    key={i}
                    className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-graphite-300 hover:border-primary-500/50 hover:text-white transition-all cursor-pointer"
                  >
                    {partner}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Card variant="premium" className="p-12">
              <h2 className="heading-md text-white mb-4">Partner With Our Group</h2>
              <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
                Explore partnership opportunities across our diverse group of companies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" asChild>
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/franchise">Franchise Program</Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  )
}